import React, { useState, useMemo } from 'react';
import {
  Search,
  Filter,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Eye,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RotateCcw,
  Download,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ShieldCheck,
  UserCheck,
  UserX,
  ShieldAlert,
  Copy,
  Mail,
  Phone,
  MapPin,
  ExternalLink,
  Users,
  SlidersHorizontal,
  X,
  MoreVertical,
  Calendar,
  Sparkles
} from 'lucide-react';
import { AdminUserRecord } from '../../lib/paymentService';

interface UserDataTableProps {
  users: AdminUserRecord[];
  stats: {
    totalUsers: number;
    activeUsers: number;
    inactiveUsers: number;
    suspendedUsers: number;
    companionsCount?: number;
    clientsCount?: number;
  };
  isLoading: boolean;
  onRefresh: () => void;
  onSelectUser: (user: AdminUserRecord) => void;
  onUpdateStatus: (userId: string, status: 'Active' | 'Inactive' | 'Suspended') => Promise<void>;
  onBatchUpdateStatus?: (userIds: string[], status: 'Active' | 'Inactive' | 'Suspended') => Promise<void>;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  roleFilter: string;
  setRoleFilter: (role: string) => void;
  statusFilter: string;
  setStatusFilter: (status: string) => void;
}

type SortField =
  | 'name'
  | 'email'
  | 'role'
  | 'accountStatus'
  | 'membershipPlan'
  | 'companionPaymentStatus'
  | 'city'
  | 'registeredAt'
  | 'id';

type SortDirection = 'asc' | 'desc';

export const UserDataTable: React.FC<UserDataTableProps> = ({
  users,
  stats,
  isLoading,
  onRefresh,
  onSelectUser,
  onUpdateStatus,
  onBatchUpdateStatus,
  searchQuery,
  setSearchQuery,
  roleFilter,
  setRoleFilter,
  statusFilter,
  setStatusFilter,
}) => {
  // Specific search target field: 'all' | 'name' | 'email' | 'status' | 'city'
  const [searchTarget, setSearchTarget] = useState<'all' | 'name' | 'email' | 'status' | 'city'>('all');

  // Additional in-table filters
  const [kycFilter, setKycFilter] = useState<'ALL' | 'VERIFIED' | 'UNVERIFIED'>('ALL');
  const [membershipFilter, setMembershipFilter] = useState<string>('ALL');

  // Sorting
  const [sortField, setSortField] = useState<SortField>('registeredAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // Row selection
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [isBatchUpdating, setIsBatchUpdating] = useState<boolean>(false);
  const [copiedEmailId, setCopiedEmailId] = useState<string | null>(null);

  // Status dropdown open state per row
  const [openStatusMenuId, setOpenStatusMenuId] = useState<string | null>(null);

  // Toggle sorting on column click
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  // Copy email to clipboard helper
  const handleCopyEmail = (email: string, id: string) => {
    navigator.clipboard.writeText(email);
    setCopiedEmailId(id);
    setTimeout(() => setCopiedEmailId(null), 2000);
  };

  // Helper to highlight matching text in table
  const highlightMatch = (text: string | undefined, query: string) => {
    if (!text) return '';
    if (!query || query.trim() === '') return text;

    const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return (
      <>
        {parts.map((part, index) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <mark
              key={index}
              className="bg-purple-500/30 text-purple-200 font-bold px-0.5 rounded"
            >
              {part}
            </mark>
          ) : (
            part
          )
        )}
      </>
    );
  };

  // Filter users based on table state
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      // 1. Text Search matching
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        if (searchTarget === 'name') {
          const matchName =
            user.name.toLowerCase().includes(q) ||
            user.displayName.toLowerCase().includes(q) ||
            (user.legalName && user.legalName.toLowerCase().includes(q));
          if (!matchName) return false;
        } else if (searchTarget === 'email') {
          if (!user.email.toLowerCase().includes(q)) return false;
        } else if (searchTarget === 'status') {
          if (!user.accountStatus.toLowerCase().includes(q)) return false;
        } else if (searchTarget === 'city') {
          if (!user.city.toLowerCase().includes(q)) return false;
        } else {
          // 'all'
          const match =
            user.name.toLowerCase().includes(q) ||
            user.displayName.toLowerCase().includes(q) ||
            user.email.toLowerCase().includes(q) ||
            user.accountStatus.toLowerCase().includes(q) ||
            user.role.toLowerCase().includes(q) ||
            user.city.toLowerCase().includes(q) ||
            user.id.includes(q) ||
            user.membershipPlan.toLowerCase().includes(q) ||
            (user.phone && user.phone.includes(q));
          if (!match) return false;
        }
      }

      // 2. Role filter
      if (roleFilter !== 'ALL' && user.role.toLowerCase() !== roleFilter.toLowerCase()) {
        return false;
      }

      // 3. Status filter
      if (statusFilter !== 'ALL' && user.accountStatus.toUpperCase() !== statusFilter.toUpperCase()) {
        return false;
      }

      // 4. KYC Filter
      if (kycFilter === 'VERIFIED' && !user.isIdVerified) return false;
      if (kycFilter === 'UNVERIFIED' && user.isIdVerified) return false;

      // 5. Membership Filter
      if (membershipFilter !== 'ALL' && user.membershipPlan !== membershipFilter) {
        return false;
      }

      return true;
    });
  }, [users, searchQuery, searchTarget, roleFilter, statusFilter, kycFilter, membershipFilter]);

  // Sort filtered users
  const sortedUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) => {
      let aVal: any = a[sortField] || '';
      let bVal: any = b[sortField] || '';

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredUsers, sortField, sortDirection]);

  // Paginated users
  const totalPages = Math.ceil(sortedUsers.length / pageSize) || 1;
  const validCurrentPage = Math.min(Math.max(currentPage, 1), totalPages);
  const startIndex = (validCurrentPage - 1) * pageSize;
  const paginatedUsers = sortedUsers.slice(startIndex, startIndex + pageSize);

  // Multi-selection helpers
  const isAllPageSelected =
    paginatedUsers.length > 0 && paginatedUsers.every((u) => selectedUserIds.has(u.id));
  const isSomePageSelected =
    paginatedUsers.some((u) => selectedUserIds.has(u.id)) && !isAllPageSelected;

  const handleToggleSelectAllPage = () => {
    const next = new Set(selectedUserIds);
    if (isAllPageSelected) {
      paginatedUsers.forEach((u) => next.delete(u.id));
    } else {
      paginatedUsers.forEach((u) => next.add(u.id));
    }
    setSelectedUserIds(next);
  };

  const handleToggleSelectRow = (userId: string) => {
    const next = new Set(selectedUserIds);
    if (next.has(userId)) {
      next.delete(userId);
    } else {
      next.add(userId);
    }
    setSelectedUserIds(next);
  };

  // Batch status update execution
  const handleBatchStatus = async (status: 'Active' | 'Inactive' | 'Suspended') => {
    if (selectedUserIds.size === 0) return;
    const confirmMsg = `Are you sure you want to set ${selectedUserIds.size} user account(s) to "${status}"?`;
    if (!window.confirm(confirmMsg)) return;

    setIsBatchUpdating(true);
    try {
      const ids = Array.from(selectedUserIds);
      if (onBatchUpdateStatus) {
        await onBatchUpdateStatus(ids, status);
      } else {
        for (const id of ids) {
          await onUpdateStatus(id, status);
        }
      }
      setSelectedUserIds(new Set());
    } finally {
      setIsBatchUpdating(false);
    }
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchQuery('');
    setSearchTarget('all');
    setRoleFilter('ALL');
    setStatusFilter('ALL');
    setKycFilter('ALL');
    setMembershipFilter('ALL');
    setCurrentPage(1);
  };

  // Active filters count
  const activeFiltersCount =
    (searchQuery.trim() ? 1 : 0) +
    (roleFilter !== 'ALL' ? 1 : 0) +
    (statusFilter !== 'ALL' ? 1 : 0) +
    (kycFilter !== 'ALL' ? 1 : 0) +
    (membershipFilter !== 'ALL' ? 1 : 0);

  // Export to CSV helper
  const handleExportCSV = () => {
    if (sortedUsers.length === 0) return;

    const headers = [
      'UID',
      'Name',
      'Display Name',
      'Email',
      'Phone',
      'Role',
      'Account Status',
      'Membership Plan',
      'Membership Status',
      'Payment Status',
      'KYC Verified',
      'City',
      'Registered At',
      'Last Login'
    ];

    const rows = sortedUsers.map((u) => [
      `"${u.id}"`,
      `"${u.name.replace(/"/g, '""')}"`,
      `"${(u.displayName || '').replace(/"/g, '""')}"`,
      `"${u.email}"`,
      `"${u.phone || ''}"`,
      `"${u.role.toUpperCase()}"`,
      `"${u.accountStatus}"`,
      `"${u.membershipPlan}"`,
      `"${u.membershipStatus}"`,
      `"${u.companionPaymentStatus}"`,
      `"${u.isIdVerified ? 'YES' : 'NO'}"`,
      `"${(u.city || '').replace(/"/g, '""')}"`,
      `"${u.registeredAt || ''}"`,
      `"${u.lastLogin || ''}"`
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `pairup_users_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Sort indicator helper
  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3.5 h-3.5 text-slate-600 group-hover:text-slate-400 transition-colors" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="w-3.5 h-3.5 text-purple-400 font-bold" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 text-purple-400 font-bold" />
    );
  };

  return (
    <div className="space-y-4">
      {/* ========================================================= */}
      {/* 1. TOP INTERACTIVE METRICS & STATUS CHIPS */}
      {/* ========================================================= */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {/* Total Users Chip */}
        <button
          type="button"
          onClick={() => {
            setStatusFilter('ALL');
            setCurrentPage(1);
          }}
          className={`p-3.5 rounded-2xl border text-left transition-all cursor-pointer relative overflow-hidden ${
            statusFilter === 'ALL'
              ? 'bg-purple-950/40 border-purple-500/80 shadow-lg shadow-purple-950/30'
              : 'bg-slate-900 border-slate-800 hover:border-slate-700'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider font-mono">
              All Users
            </span>
            <Users className="w-4 h-4 text-purple-400" />
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-black text-white font-mono">{stats.totalUsers}</span>
            <span className="text-[10px] text-slate-400">Total accounts</span>
          </div>
          {statusFilter === 'ALL' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-500" />
          )}
        </button>

        {/* Active Accounts Chip */}
        <button
          type="button"
          onClick={() => {
            setStatusFilter('Active');
            setCurrentPage(1);
          }}
          className={`p-3.5 rounded-2xl border text-left transition-all cursor-pointer relative overflow-hidden ${
            statusFilter === 'Active'
              ? 'bg-emerald-950/50 border-emerald-500 shadow-lg shadow-emerald-950/40'
              : 'bg-slate-900 border-slate-800 hover:border-emerald-900/60'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-emerald-400 uppercase tracking-wider font-mono flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              Active
            </span>
            <UserCheck className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-black text-emerald-300 font-mono">
              {stats.activeUsers}
            </span>
            <span className="text-[10px] text-emerald-400/80">Verified & Live</span>
          </div>
          {statusFilter === 'Active' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500" />
          )}
        </button>

        {/* Inactive Accounts Chip */}
        <button
          type="button"
          onClick={() => {
            setStatusFilter('Inactive');
            setCurrentPage(1);
          }}
          className={`p-3.5 rounded-2xl border text-left transition-all cursor-pointer relative overflow-hidden ${
            statusFilter === 'Inactive'
              ? 'bg-amber-950/50 border-amber-500 shadow-lg shadow-amber-950/40'
              : 'bg-slate-900 border-slate-800 hover:border-amber-900/60'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-amber-400 uppercase tracking-wider font-mono flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-400" />
              Inactive
            </span>
            <UserX className="w-4 h-4 text-amber-400" />
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-black text-amber-300 font-mono">
              {stats.inactiveUsers}
            </span>
            <span className="text-[10px] text-amber-400/80">Unverified / Unpaid</span>
          </div>
          {statusFilter === 'Inactive' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-500" />
          )}
        </button>

        {/* Suspended Accounts Chip */}
        <button
          type="button"
          onClick={() => {
            setStatusFilter('Suspended');
            setCurrentPage(1);
          }}
          className={`p-3.5 rounded-2xl border text-left transition-all cursor-pointer relative overflow-hidden ${
            statusFilter === 'Suspended'
              ? 'bg-rose-950/50 border-rose-500 shadow-lg shadow-rose-950/40'
              : 'bg-slate-900 border-slate-800 hover:border-rose-900/60'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-rose-400 uppercase tracking-wider font-mono flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-rose-400" />
              Suspended
            </span>
            <ShieldAlert className="w-4 h-4 text-rose-400" />
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-black text-rose-300 font-mono">
              {stats.suspendedUsers}
            </span>
            <span className="text-[10px] text-rose-400/80">Restricted access</span>
          </div>
          {statusFilter === 'Suspended' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-rose-500" />
          )}
        </button>
      </div>

      {/* ========================================================= */}
      {/* 2. SEARCH & FILTER CONTROLS TOOLBAR */}
      {/* ========================================================= */}
      <div className="bg-slate-900 rounded-3xl border border-slate-800 p-4 space-y-3 shadow-xl">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
          {/* Omni Search with Target Scope selector */}
          <div className="flex-1 flex items-center gap-2">
            {/* Search target selector dropdown */}
            <div className="relative shrink-0">
              <select
                value={searchTarget}
                onChange={(e) => {
                  setSearchTarget(e.target.value as any);
                  setCurrentPage(1);
                }}
                className="bg-slate-950 border border-slate-800 hover:border-purple-600 rounded-xl px-3 py-2 text-xs text-slate-300 font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
                title="Search specifically by field"
              >
                <option value="all">🔍 Search: All Fields</option>
                <option value="name">👤 Search by Name</option>
                <option value="email">✉️ Search by Email</option>
                <option value="status">⚡ Search by Status</option>
                <option value="city">📍 Search by City</option>
              </select>
            </div>

            {/* Input field with Clear button */}
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder={
                  searchTarget === 'name'
                    ? 'Type user name, display name, legal name...'
                    : searchTarget === 'email'
                    ? 'Type user email address...'
                    : searchTarget === 'status'
                    ? 'Type status: Active, Inactive, Suspended...'
                    : searchTarget === 'city'
                    ? 'Type city location (e.g. Mumbai, Kolkata, Delhi)...'
                    : 'Search across name, email, status, phone, city, UID...'
                }
                className="w-full pl-10 pr-9 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
                  title="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Quick Filters Group */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Role Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-bold text-slate-400">Role:</span>
              <select
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="bg-slate-950 border border-slate-800 rounded-xl px-2.5 py-1.5 text-xs text-white focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
              >
                <option value="ALL">All Roles</option>
                <option value="companion">Companions Only</option>
                <option value="client">Clients Only</option>
              </select>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-bold text-slate-400">Status:</span>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="bg-slate-950 border border-slate-800 rounded-xl px-2.5 py-1.5 text-xs text-white focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
              >
                <option value="ALL">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Suspended">Suspended</option>
              </select>
            </div>

            {/* KYC Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-bold text-slate-400">KYC:</span>
              <select
                value={kycFilter}
                onChange={(e) => {
                  setKycFilter(e.target.value as any);
                  setCurrentPage(1);
                }}
                className="bg-slate-950 border border-slate-800 rounded-xl px-2.5 py-1.5 text-xs text-white focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
              >
                <option value="ALL">All KYC</option>
                <option value="VERIFIED">Verified ID</option>
                <option value="UNVERIFIED">Unverified</option>
              </select>
            </div>

            {/* Membership Plan Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-bold text-slate-400">Plan:</span>
              <select
                value={membershipFilter}
                onChange={(e) => {
                  setMembershipFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="bg-slate-950 border border-slate-800 rounded-xl px-2.5 py-1.5 text-xs text-white focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
              >
                <option value="ALL">All Plans</option>
                <option value="STARTER">Starter</option>
                <option value="PROFESSIONAL">Professional</option>
                <option value="PREMIUM">Premium</option>
                <option value="FREE">Free</option>
              </select>
            </div>

            {/* Reset All Filters button */}
            {activeFiltersCount > 0 && (
              <button
                type="button"
                onClick={handleResetFilters}
                className="px-2.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1"
                title="Reset all filters"
              >
                <X className="w-3.5 h-3.5 text-rose-400" />
                <span>Reset ({activeFiltersCount})</span>
              </button>
            )}

            {/* CSV Export Button */}
            <button
              type="button"
              onClick={handleExportCSV}
              disabled={sortedUsers.length === 0}
              className="px-3 py-1.5 bg-slate-800 hover:bg-purple-900/60 hover:text-purple-300 border border-slate-700 rounded-xl text-xs font-bold text-slate-200 transition-all cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
              title="Download filtered user table as CSV"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Export CSV</span>
            </button>

            {/* Refresh Button */}
            <button
              type="button"
              onClick={onRefresh}
              disabled={isLoading}
              className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl transition-all cursor-pointer"
              title="Reload user records from database"
            >
              <RotateCcw className={`w-4 h-4 ${isLoading ? 'animate-spin text-purple-400' : ''}`} />
            </button>
          </div>
        </div>

        {/* Active Filter Badges Bar */}
        {activeFiltersCount > 0 && (
          <div className="pt-2 border-t border-slate-800 flex items-center gap-2 flex-wrap text-[11px]">
            <span className="text-slate-500 font-mono">Active filters:</span>
            {searchQuery && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-purple-950 border border-purple-800 text-purple-300 font-medium">
                Search: "{searchQuery}" ({searchTarget})
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="hover:text-white cursor-pointer ml-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {roleFilter !== 'ALL' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-blue-950 border border-blue-800 text-blue-300 font-medium">
                Role: {roleFilter}
                <button
                  type="button"
                  onClick={() => setRoleFilter('ALL')}
                  className="hover:text-white cursor-pointer ml-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {statusFilter !== 'ALL' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-950 border border-emerald-800 text-emerald-300 font-medium">
                Status: {statusFilter}
                <button
                  type="button"
                  onClick={() => setStatusFilter('ALL')}
                  className="hover:text-white cursor-pointer ml-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {kycFilter !== 'ALL' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-slate-300 font-medium">
                KYC: {kycFilter}
                <button
                  type="button"
                  onClick={() => setKycFilter('ALL')}
                  className="hover:text-white cursor-pointer ml-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {membershipFilter !== 'ALL' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-slate-300 font-medium">
                Plan: {membershipFilter}
                <button
                  type="button"
                  onClick={() => setMembershipFilter('ALL')}
                  className="hover:text-white cursor-pointer ml-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* ========================================================= */}
      {/* 3. BATCH ACTIONS TOOLBAR (WHEN ROWS ARE SELECTED) */}
      {/* ========================================================= */}
      {selectedUserIds.size > 0 && (
        <div className="p-3 bg-purple-950/80 border border-purple-700/80 rounded-2xl flex flex-wrap items-center justify-between gap-3 animate-in fade-in slide-in-from-top-2 duration-150 shadow-xl">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-purple-600 text-white font-mono font-bold text-xs flex items-center justify-center">
              {selectedUserIds.size}
            </span>
            <span className="text-xs font-bold text-white">
              {selectedUserIds.size === 1 ? '1 user account selected' : `${selectedUserIds.size} user accounts selected`}
            </span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-bold text-purple-300">Bulk Actions:</span>
            <button
              type="button"
              disabled={isBatchUpdating}
              onClick={() => handleBatchStatus('Active')}
              className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1 disabled:opacity-50"
            >
              <Check className="w-3.5 h-3.5" />
              <span>Activate Selected</span>
            </button>

            <button
              type="button"
              disabled={isBatchUpdating}
              onClick={() => handleBatchStatus('Inactive')}
              className="px-2.5 py-1 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1 disabled:opacity-50"
            >
              <UserX className="w-3.5 h-3.5" />
              <span>Deactivate Selected</span>
            </button>

            <button
              type="button"
              disabled={isBatchUpdating}
              onClick={() => handleBatchStatus('Suspended')}
              className="px-2.5 py-1 bg-rose-600 hover:bg-rose-500 text-white rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1 disabled:opacity-50"
            >
              <ShieldAlert className="w-3.5 h-3.5" />
              <span>Suspend Selected</span>
            </button>

            <button
              type="button"
              onClick={() => setSelectedUserIds(new Set())}
              className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-bold transition-all cursor-pointer"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* 4. THE DATA TABLE */}
      {/* ========================================================= */}
      <div className="bg-slate-900 rounded-3xl border border-slate-800 shadow-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-slate-950 border-b border-slate-800 text-[10px] font-mono font-bold uppercase text-slate-400 tracking-wider select-none">
                {/* Checkbox column */}
                <th className="py-3.5 px-4 w-10 text-center">
                  <input
                    type="checkbox"
                    checked={isAllPageSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = isSomePageSelected;
                    }}
                    onChange={handleToggleSelectAllPage}
                    className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-purple-600 focus:ring-purple-500 cursor-pointer"
                    title="Select all on this page"
                  />
                </th>

                {/* User / Name column */}
                <th
                  onClick={() => handleSort('name')}
                  className="py-3.5 px-4 cursor-pointer hover:text-white transition-colors group"
                >
                  <div className="flex items-center gap-1.5">
                    <span>User / Account</span>
                    {renderSortIndicator('name')}
                  </div>
                </th>

                {/* Email / Contact column */}
                <th
                  onClick={() => handleSort('email')}
                  className="py-3.5 px-4 cursor-pointer hover:text-white transition-colors group"
                >
                  <div className="flex items-center gap-1.5">
                    <span>Email & Contact</span>
                    {renderSortIndicator('email')}
                  </div>
                </th>

                {/* Role column */}
                <th
                  onClick={() => handleSort('role')}
                  className="py-3.5 px-4 cursor-pointer hover:text-white transition-colors group"
                >
                  <div className="flex items-center gap-1.5">
                    <span>Role</span>
                    {renderSortIndicator('role')}
                  </div>
                </th>

                {/* Account Status column */}
                <th
                  onClick={() => handleSort('accountStatus')}
                  className="py-3.5 px-4 cursor-pointer hover:text-white transition-colors group"
                >
                  <div className="flex items-center gap-1.5">
                    <span>Account Status</span>
                    {renderSortIndicator('accountStatus')}
                  </div>
                </th>

                {/* Membership Plan column */}
                <th
                  onClick={() => handleSort('membershipPlan')}
                  className="py-3.5 px-4 cursor-pointer hover:text-white transition-colors group"
                >
                  <div className="flex items-center gap-1.5">
                    <span>Plan Tier</span>
                    {renderSortIndicator('membershipPlan')}
                  </div>
                </th>

                {/* Companion Payment Status */}
                <th
                  onClick={() => handleSort('companionPaymentStatus')}
                  className="py-3.5 px-4 cursor-pointer hover:text-white transition-colors group"
                >
                  <div className="flex items-center gap-1.5">
                    <span>Payment Status</span>
                    {renderSortIndicator('companionPaymentStatus')}
                  </div>
                </th>

                {/* City column */}
                <th
                  onClick={() => handleSort('city')}
                  className="py-3.5 px-4 cursor-pointer hover:text-white transition-colors group"
                >
                  <div className="flex items-center gap-1.5">
                    <span>City</span>
                    {renderSortIndicator('city')}
                  </div>
                </th>

                {/* Actions */}
                <th className="py-3.5 px-4 text-right">Admin Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-800/80">
              {paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 px-4 text-center">
                    <div className="max-w-md mx-auto space-y-3">
                      <div className="w-12 h-12 rounded-2xl bg-slate-800 text-slate-400 flex items-center justify-center mx-auto">
                        <Search className="w-6 h-6" />
                      </div>
                      <h4 className="text-base font-bold text-white">No user accounts found</h4>
                      <p className="text-xs text-slate-400 leading-relaxed">
                        {searchQuery
                          ? `No users matched "${searchQuery}" with the selected filters.`
                          : 'No users exist matching the specified role or status filter.'}
                      </p>
                      <button
                        type="button"
                        onClick={handleResetFilters}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold transition-all cursor-pointer inline-flex items-center gap-1.5"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        <span>Clear All Filters & Show All Users</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((u) => {
                  const isSelected = selectedUserIds.has(u.id);
                  const isCompanion = u.role === 'companion';

                  return (
                    <tr
                      key={u.id}
                      className={`transition-colors ${
                        isSelected
                          ? 'bg-purple-950/20 hover:bg-purple-950/30'
                          : 'hover:bg-slate-850/80'
                      }`}
                    >
                      {/* Checkbox */}
                      <td className="py-3 px-4 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelectRow(u.id)}
                          className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-purple-600 focus:ring-purple-500 cursor-pointer"
                        />
                      </td>

                      {/* User / Name */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div className="relative shrink-0">
                            <img
                              src={
                                u.avatarUrl ||
                                'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=100&q=80'
                              }
                              alt={u.name}
                              className="w-10 h-10 rounded-xl object-cover border border-slate-700 shrink-0"
                            />
                            {/* Live status dot on avatar */}
                            <span
                              className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-slate-900 ${
                                u.accountStatus === 'Active'
                                  ? 'bg-emerald-400'
                                  : u.accountStatus === 'Suspended'
                                  ? 'bg-rose-400'
                                  : 'bg-amber-400'
                              }`}
                            />
                          </div>

                          <div>
                            <div className="font-bold text-white flex items-center gap-1.5">
                              <span>{highlightMatch(u.name, searchQuery)}</span>
                              {u.isIdVerified ? (
                                <span title="KYC Government ID Verified" className="text-emerald-400">
                                  <CheckCircle2 className="w-3.5 h-3.5" />
                                </span>
                              ) : (
                                <span title="ID Not Verified" className="text-slate-500">
                                  <ShieldAlert className="w-3.5 h-3.5" />
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-400">
                              Display: {highlightMatch(u.displayName, searchQuery)}
                            </div>
                            <div className="text-[10px] text-slate-500 font-mono flex items-center gap-1">
                              <span>UID:</span>
                              <span className="text-purple-300 font-bold">{u.id}</span>
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Email & Contact */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-1 text-slate-300 font-mono text-[11px]">
                            <Mail className="w-3 h-3 text-slate-500 shrink-0" />
                            <span>{highlightMatch(u.email, searchQuery)}</span>
                            <button
                              type="button"
                              onClick={() => handleCopyEmail(u.email, u.id)}
                              className="p-1 text-slate-500 hover:text-slate-300 cursor-pointer rounded"
                              title="Copy email to clipboard"
                            >
                              {copiedEmailId === u.id ? (
                                <Check className="w-3 h-3 text-emerald-400" />
                              ) : (
                                <Copy className="w-3 h-3" />
                              )}
                            </button>
                          </div>
                          {u.phone && (
                            <div className="flex items-center gap-1 text-[11px] text-slate-400 font-mono">
                              <Phone className="w-3 h-3 text-slate-500 shrink-0" />
                              <span>{highlightMatch(u.phone, searchQuery)}</span>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Role */}
                      <td className="py-3.5 px-4">
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider font-mono ${
                            isCompanion
                              ? 'bg-purple-900/60 border border-purple-700 text-purple-300'
                              : 'bg-blue-900/60 border border-blue-700 text-blue-300'
                          }`}
                        >
                          {u.role.toUpperCase()}
                        </span>
                      </td>

                      {/* Account Status with Dropdown Pill */}
                      <td className="py-3.5 px-4">
                        <div className="relative inline-block text-left">
                          <button
                            type="button"
                            onClick={() =>
                              setOpenStatusMenuId(openStatusMenuId === u.id ? null : u.id)
                            }
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold font-mono border transition-all cursor-pointer ${
                              u.accountStatus === 'Active'
                                ? 'bg-emerald-950 border-emerald-700 text-emerald-400 hover:bg-emerald-900/60'
                                : u.accountStatus === 'Suspended'
                                ? 'bg-rose-950 border-rose-700 text-rose-400 hover:bg-rose-900/60'
                                : 'bg-amber-950 border-amber-700 text-amber-400 hover:bg-amber-900/60'
                            }`}
                            title="Click to toggle or change status"
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${
                                u.accountStatus === 'Active'
                                  ? 'bg-emerald-400'
                                  : u.accountStatus === 'Suspended'
                                  ? 'bg-rose-400'
                                  : 'bg-amber-400'
                              }`}
                            />
                            <span>{highlightMatch(u.accountStatus, searchQuery)}</span>
                            <MoreVertical className="w-3 h-3 text-slate-400" />
                          </button>

                          {/* Inline Status Menu */}
                          {openStatusMenuId === u.id && (
                            <div className="absolute left-0 mt-1 w-36 bg-slate-950 border border-slate-700 rounded-xl shadow-2xl py-1 z-20 animate-in fade-in duration-100">
                              <button
                                type="button"
                                onClick={() => {
                                  onUpdateStatus(u.id, 'Active');
                                  setOpenStatusMenuId(null);
                                }}
                                className="w-full text-left px-3 py-1.5 text-xs text-emerald-400 hover:bg-slate-800 flex items-center gap-2 cursor-pointer font-bold"
                              >
                                <Check className="w-3.5 h-3.5" />
                                <span>Set Active</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  onUpdateStatus(u.id, 'Inactive');
                                  setOpenStatusMenuId(null);
                                }}
                                className="w-full text-left px-3 py-1.5 text-xs text-amber-400 hover:bg-slate-800 flex items-center gap-2 cursor-pointer font-bold"
                              >
                                <UserX className="w-3.5 h-3.5" />
                                <span>Set Inactive</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  onUpdateStatus(u.id, 'Suspended');
                                  setOpenStatusMenuId(null);
                                }}
                                className="w-full text-left px-3 py-1.5 text-xs text-rose-400 hover:bg-slate-800 flex items-center gap-2 cursor-pointer font-bold"
                              >
                                <ShieldAlert className="w-3.5 h-3.5" />
                                <span>Suspend</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Membership Plan Tier */}
                      <td className="py-3.5 px-4">
                        <div className="font-mono font-bold text-slate-200">
                          {u.membershipPlan}
                        </div>
                        <div className="text-[10px] text-slate-400 font-mono">
                          {u.membershipStatus}
                        </div>
                      </td>

                      {/* Companion Payment Status */}
                      <td className="py-3.5 px-4">
                        {isCompanion ? (
                          <span
                            className={`inline-flex items-center gap-1 text-[11px] font-bold ${
                              u.companionPaymentStatus === 'Success'
                                ? 'text-emerald-400'
                                : u.companionPaymentStatus === 'Pending'
                                ? 'text-amber-400'
                                : 'text-rose-400'
                            }`}
                          >
                            {u.companionPaymentStatus === 'Success' && <CheckCircle2 className="w-3.5 h-3.5" />}
                            {u.companionPaymentStatus === 'Pending' && <AlertTriangle className="w-3.5 h-3.5" />}
                            {u.companionPaymentStatus === 'Failed' && <XCircle className="w-3.5 h-3.5" />}
                            <span>
                              {u.companionPaymentStatus === 'Success'
                                ? 'Verified'
                                : u.companionPaymentStatus === 'Pending'
                                ? 'Pending'
                                : 'Failed / Unpaid'}
                            </span>
                          </span>
                        ) : (
                          <span className="text-[11px] text-slate-500 font-mono">Client Account</span>
                        )}
                      </td>

                      {/* City */}
                      <td className="py-3.5 px-4 text-slate-400">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-slate-500 shrink-0" />
                          <span>{highlightMatch(u.city, searchQuery)}</span>
                        </div>
                      </td>

                      {/* Admin Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* View details */}
                          <button
                            type="button"
                            onClick={() => onSelectUser(u)}
                            className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1"
                            title="Inspect user details, KYC, and payment logs"
                          >
                            <Eye className="w-3.5 h-3.5 text-purple-400" />
                            <span>Details</span>
                          </button>

                          {/* Quick In-line Status toggle */}
                          {u.accountStatus === 'Active' ? (
                            <button
                              type="button"
                              onClick={() => onUpdateStatus(u.id, 'Inactive')}
                              className="px-2 py-1 bg-amber-950/80 hover:bg-amber-900 border border-amber-800 text-amber-300 rounded-lg text-[11px] font-bold transition-all cursor-pointer"
                              title="Deactivate account"
                            >
                              Deactivate
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => onUpdateStatus(u.id, 'Active')}
                              className="px-2 py-1 bg-emerald-950/80 hover:bg-emerald-900 border border-emerald-800 text-emerald-300 rounded-lg text-[11px] font-bold transition-all cursor-pointer"
                              title="Activate account"
                            >
                              Activate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ========================================================= */}
        {/* 5. TABLE FOOTER: PAGINATION & RECORD COUNTER */}
        {/* ========================================================= */}
        <div className="p-4 bg-slate-950 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
          {/* Record summary */}
          <div className="text-slate-400 text-[11px] flex items-center gap-2">
            <span>
              Showing{' '}
              <strong className="text-white font-mono">
                {sortedUsers.length > 0 ? startIndex + 1 : 0}
              </strong>{' '}
              to{' '}
              <strong className="text-white font-mono">
                {Math.min(startIndex + pageSize, sortedUsers.length)}
              </strong>{' '}
              of{' '}
              <strong className="text-white font-mono">{sortedUsers.length}</strong> user accounts
            </span>
            {sortedUsers.length !== users.length && (
              <span className="text-slate-500 font-mono">
                (filtered from {users.length} total)
              </span>
            )}
          </div>

          {/* Page size & Pagination navigation */}
          <div className="flex items-center gap-3">
            {/* Rows per page selector */}
            <div className="flex items-center gap-1.5">
              <span className="text-slate-500 text-[11px]">Rows:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="bg-slate-900 border border-slate-800 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:ring-1 focus:ring-purple-500 cursor-pointer"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>

            {/* Page navigation buttons */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={validCurrentPage <= 1}
                onClick={() => setCurrentPage(1)}
                className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-850 text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                title="First page"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                disabled={validCurrentPage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-850 text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                title="Previous page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <span className="px-3 py-1 font-mono font-bold text-white text-xs bg-slate-900 rounded-lg border border-slate-800">
                {validCurrentPage} / {totalPages}
              </span>

              <button
                type="button"
                disabled={validCurrentPage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-850 text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                title="Next page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                disabled={validCurrentPage >= totalPages}
                onClick={() => setCurrentPage(totalPages)}
                className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-850 text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                title="Last page"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
